/**
 * Custom remark plugin to transform Obsidian-style callouts into hast-compatible nodes.
 *
 * Obsidian callout syntax:
 *   > [!info] Optional Title
 *   > Body text here.
 *
 * This plugin walks the mdast blockquote nodes, detects the `> [!type]` first line,
 * and rewrites the node with a `data` property that rehype-react can pick up to
 * render a styled <div> instead of a plain <blockquote>.
 *
 * Supported callout types:
 *   note, info, tip, hint, important, warning, caution, attention,
 *   danger, error, bug, example, quote, abstract, summary, tldr, success, check, done, question, help, faq, failure, fail, missing
 *
 * The transformed node keeps `type: 'blockquote'` but attaches
 *   data.hName   = 'div'
 *   data.hProperties = { 'data-callout': '<type>', className: ['callout', 'callout-<type>'] }
 * and injects a heading <div> as the first child.
 */

import type { Plugin } from 'unified';
import type { Root, Blockquote, Paragraph, Text, PhrasingContent } from 'mdast';
import { visit } from 'unist-util-visit';

export type CalloutType =
  | 'note'
  | 'info'
  | 'tip'
  | 'hint'
  | 'important'
  | 'warning'
  | 'caution'
  | 'attention'
  | 'danger'
  | 'error'
  | 'bug'
  | 'example'
  | 'quote'
  | 'abstract'
  | 'summary'
  | 'tldr'
  | 'success'
  | 'check'
  | 'done'
  | 'question'
  | 'help'
  | 'faq'
  | 'failure'
  | 'fail'
  | 'missing';

const CALLOUT_PATTERN = /^\[!([a-zA-Z-]+)\][\t ]*(.*)/;

/** Icons per callout type — simple SVG-free text fallbacks */
const CALLOUT_ICONS: Record<string, string> = {
  note: '📝',
  info: 'ℹ️',
  tip: '💡',
  hint: '💡',
  important: '❗',
  warning: '⚠️',
  caution: '⚠️',
  attention: '⚠️',
  danger: '🔥',
  error: '❌',
  bug: '🐛',
  example: '📋',
  quote: '❝',
  abstract: '📄',
  summary: '📄',
  tldr: '📄',
  success: '✅',
  check: '✅',
  done: '✅',
  question: '❓',
  help: '❓',
  faq: '❓',
  failure: '❎',
  fail: '❎',
  missing: '❎',
};

const DEFAULT_ICON = '📌';

/**
 * Extract the plain text content from a list of phrasing content nodes.
 * We need this to grab the `[!type] Title` text from the first blockquote paragraph.
 */
function extractText(nodes: PhrasingContent[]): string {
  return nodes
    .map((n) => {
      if (n.type === 'text') return (n as Text).value;
      if ('children' in n) return extractText((n as { children: PhrasingContent[] }).children);
      return '';
    })
    .join('');
}

const remarkCallouts: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, 'blockquote', (node: Blockquote) => {
      // The first child must be a paragraph
      const firstChild = node.children[0];
      if (!firstChild || firstChild.type !== 'paragraph') return;

      const para = firstChild as Paragraph;
      const rawText = extractText(para.children);

      const match = rawText.match(CALLOUT_PATTERN);
      if (!match) return;

      const calloutType = match[1].toLowerCase() as CalloutType;
      const calloutTitle = match[2].trim() || capitalize(calloutType);
      const icon = CALLOUT_ICONS[calloutType] ?? DEFAULT_ICON;

      // How remark actually parses `> [!type] Title\n> Body text` (no blank line):
      //
      // The entire blockquote becomes ONE paragraph with ONE text node whose value
      // is the multiline string "..Title..\nBody text here". Remark does NOT emit
      // a `break` node here — the newline is literally embedded in the text value.
      //
      // Strategy:
      //   1. Take the first text node of para.children and split it on the first `\n`.
      //      Everything before the newline is the title line (already matched).
      //      Everything after the newline is the start of the body text.
      //   2. If there is body text, rebuild the first para's children: replace the
      //      original text node with a new one containing only the post-newline text,
      //      then keep any subsequent sibling phrasing nodes (there rarely are any,
      //      but handle them for correctness).
      //   3. Wrap the resulting phrasing nodes in a new paragraph block node.
      //   4. Also collect any additional block-level children from node.children[1..]
      //      (these exist when a blank `>` line separates body paragraphs).

      const firstTextNode = para.children[0] as Text;
      const firstTextValue: string = firstTextNode?.type === 'text' ? firstTextNode.value : '';

      // Split on the first newline to separate title line from body continuation
      const newlineIdx = firstTextValue.indexOf('\n');
      const bodyTextAfterTitle = newlineIdx !== -1 ? firstTextValue.slice(newlineIdx + 1) : '';

      // Phrasing siblings after the first text node (uncommon, but possible with
      // inline markup like `> [!info] **Title**\n> body`)
      const siblingPhrasingNodes = para.children.slice(1);

      // Build phrasing children for the body paragraph
      const bodyPhrasingNodes: PhrasingContent[] = [];
      if (bodyTextAfterTitle) {
        bodyPhrasingNodes.push({ type: 'text', value: bodyTextAfterTitle });
      }
      bodyPhrasingNodes.push(...siblingPhrasingNodes);

      // Block-level children of the blockquote beyond the first paragraph
      // (present when body has multiple paragraphs separated by a blank `>` line)
      const blockBodyChildren = node.children.slice(1);

      // Assemble the final list of block nodes for the body wrapper
      const bodyBlockNodes: Blockquote['children'] = [];

      if (bodyPhrasingNodes.length > 0) {
        bodyBlockNodes.push({ type: 'paragraph', children: bodyPhrasingNodes });
      }

      bodyBlockNodes.push(...blockBodyChildren);

      // Rewrite in place — mutate the node to be a callout div via hast data
      node.data = {
        ...node.data,
        hName: 'div',
        hProperties: {
          'data-callout': calloutType,
          className: `callout callout-${calloutType}`,
        },
      };

      // Title div
      const titleNode: Paragraph & { data: NonNullable<Paragraph['data']> } = {
        type: 'paragraph',
        data: {
          hName: 'div',
          hProperties: { className: 'callout-title' },
        },
        children: [
          {
            type: 'text',
            value: `${icon} ${calloutTitle}`,
          },
        ],
      };

      // Body wrapper div — only added when there is actual content
      const bodyNode: Blockquote & { data: NonNullable<Blockquote['data']> } = {
        type: 'blockquote',
        data: {
          hName: 'div',
          hProperties: { className: 'callout-body' },
        },
        children: bodyBlockNodes,
      };

      node.children = bodyBlockNodes.length > 0
        ? [titleNode, bodyNode]
        : [titleNode];
    });
  };
};

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default remarkCallouts;
